import { toNumber } from "@/shared/utils";
import { PLANNED_FIELDS } from "@/features/projects";
import type {
  PlannedField,
  ProjectDetail,
  CategoryBreakdownEntry,
  RedistributeBudgetPayload,
  ProjectIncomeSource,
} from "@/features/projects";

export type CategoryCells = Record<PlannedField, CategoryBreakdownEntry>;

export function toCents(value: string | undefined): number {
  return Math.round(toNumber(value || "") * 100);
}

export function sanitizeMoney(value: string): string {
  const cleaned = value.replace(/[^0-9.]/g, "");
  const dotIndex = cleaned.indexOf(".");
  if (dotIndex === -1) return cleaned;
  const intPart = cleaned.slice(0, dotIndex);
  const decPart = cleaned
    .slice(dotIndex + 1)
    .replace(/\./g, "")
    .slice(0, 2);
  return `${intPart}.${decPart}`;
}

export function makeEmptyInputs(): Record<PlannedField, string> {
  const base = {} as Record<PlannedField, string>;
  PLANNED_FIELDS.forEach(function setEmpty(field) {
    base[field] = "";
  });
  return base;
}

export function buildCells(detail: ProjectDetail | undefined): CategoryCells {
  const breakdown = detail?.budget_summary?.category_breakdown;
  const result = {} as CategoryCells;
  PLANNED_FIELDS.forEach(function fill(field) {
    const entry = breakdown?.[field];
    const planned = entry?.planned ?? detail?.[field] ?? "0";
    const spent = entry?.spent ?? "0";
    const available = entry?.available ?? planned;
    result[field] = { planned, spent, available };
  });
  return result;
}

export function sumInputsCents(inputs: Record<PlannedField, string>): number {
  return PLANNED_FIELDS.reduce(function add(acc, field) {
    return acc + toCents(inputs[field]);
  }, 0);
}

export interface SourceStepResult {
  totalCents: number;
  errors: Partial<Record<PlannedField, string>>;
  valid: boolean;
}

export function validateSourceStep(cells: CategoryCells, inputs: Record<PlannedField, string>): SourceStepResult {
  const errors: Partial<Record<PlannedField, string>> = {};
  let totalCents = 0;
  PLANNED_FIELDS.forEach(function check(field) {
    const valueCents = toCents(inputs[field]);
    totalCents += valueCents;
    if (valueCents > 0 && valueCents > toCents(cells[field]?.available)) {
      errors[field] = "redistribute.errors.overAvailable";
    }
  });
  const valid = totalCents > 0 && Object.keys(errors).length === 0;
  return { totalCents, errors, valid };
}

export interface TargetStepResult {
  totalCents: number;
  remainingCents: number;
  valid: boolean;
}

export function validateTargetStep(inputs: Record<PlannedField, string>, sourceTotalCents: number): TargetStepResult {
  const totalCents = sumInputsCents(inputs);
  return {
    totalCents,
    remainingCents: sourceTotalCents - totalCents,
    valid: sourceTotalCents > 0 && totalCents === sourceTotalCents,
  };
}

export interface IncomeSourcesStepResult {
  totalCents: number;
  errors: Record<number, string>;
  valid: boolean;
}

export function validateIncomeSourcesStep(
  inputs: Record<number, string>,
  availableByIncomeId: Record<number, number>,
): IncomeSourcesStepResult {
  const errors: Record<number, string> = {};
  let totalCents = 0;
  Object.entries(inputs).forEach(function check([key, value]) {
    const incomeId = Number(key);
    const valueCents = toCents(value);
    totalCents += valueCents;
    if (valueCents > 0 && valueCents > (availableByIncomeId[incomeId] ?? 0)) {
      errors[incomeId] = "redistribute.errors.overAvailable";
    }
  });
  const valid = totalCents > 0 && Object.keys(errors).length === 0;
  return { totalCents, errors, valid };
}

export type RedistributePayloadInput =
  | {
      source_kind: "project";
      source_project_id: number;
      target_project_id: number;
      sourceInputs: Record<PlannedField, string>;
      targetInputs: Record<PlannedField, string>;
    }
  | {
      source_kind: "external_pool";
      target_project_id: number;
      incomeInputs: Record<number, string>;
      targetInputs: Record<PlannedField, string>;
    };

export function buildRedistributePayload(input: RedistributePayloadInput): RedistributeBudgetPayload {
  const target_deltas: Partial<Record<PlannedField, string>> = {};
  PLANNED_FIELDS.forEach(function collect(field) {
    if (toCents(input.targetInputs[field]) > 0) target_deltas[field] = input.targetInputs[field];
  });

  if (input.source_kind === "external_pool") {
    const income_sources: ProjectIncomeSource[] = [];
    Object.entries(input.incomeInputs).forEach(function collect([key, value]) {
      if (toCents(value) > 0) income_sources.push({ income_id: Number(key), amount: value });
    });
    return {
      source_kind: "external_pool",
      target_project_id: input.target_project_id,
      income_sources,
      target_deltas,
    };
  }

  const source_deltas: Partial<Record<PlannedField, string>> = {};
  PLANNED_FIELDS.forEach(function collect(field) {
    if (toCents(input.sourceInputs[field]) > 0) source_deltas[field] = input.sourceInputs[field];
  });
  return {
    source_kind: "project",
    source_project_id: input.source_project_id,
    target_project_id: input.target_project_id,
    source_deltas,
    target_deltas,
  };
}
